<template>
    <v-dialog v-model="editBuildingDialog" max-width="767" persistent>
        <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" @click="setBuilding()" :color="successColor" icon>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon v-bind="attrs" v-on="on">
                            mdi-pencil
                        </v-icon>
                    </template>
                    <span>Edit</span>
                </v-tooltip>
            </v-btn>
        </template>
        <v-card>
            <v-card-title>
                Edit Building
            </v-card-title>
            <v-card-text>
                <v-form ref="editBuildingForm" v-model="valid" lazy-validation>
                    <v-text-field v-model="currentBuilding.name" label="Name" :rules="nameRules" required>
                    </v-text-field>
                    <v-text-field v-model="currentBuilding.maxOccupancy" label="Max Occupancy"
                        :rules="maxOccupancyRules" required type="number">
                    </v-text-field>
                    <v-select v-model="currentBuilding.maxRoommateRequest" :items="maxRoommateRequestItems"
                        label="Max Roommate Request" :rules="maxRoommateRequestRules" required>
                    </v-select>
                    <v-select v-model="currentBuilding.minYearOfStudy" :items="minYearOfStudyItems"
                        label="Min Year Of Study" :rules="minYearOfStudyRules" required>
                    </v-select>
                    <v-select v-model="currentBuilding.mealPlanRequired" :items="mealPlanRequiredItems"
                        label="Meal Plan Required" :rules="mealPlanRequiredRules" required>
                    </v-select>
                    <v-card-subtitle class="font-weight-bold pb-1 px-0">Gender Restriction</v-card-subtitle>
                    <v-checkbox v-model="currentBuilding.genderRestriction" dense hide-details label="Male"
                        value="male">
                    </v-checkbox>
                    <v-checkbox v-model="currentBuilding.genderRestriction" dense hide-details label="Female"
                        value="female">
                    </v-checkbox>
                    <v-checkbox v-model="currentBuilding.genderRestriction" dense label="Non-Binary"
                        value="NonBinary">
                    </v-checkbox>
                    <v-card-subtitle class="font-weight-bold pb-1 px-0">Additional Information</v-card-subtitle>
                    <v-card-subtitle class="px-0 py-1">
                        <b>File:</b> {{ getContractConditionsFilename() }}<br>
                        <b>Uploaded By:</b> {{ getContractConditionsUploadedBy() }}<br>
                        <b>Date:</b> {{ getContractConditionsFileDate() }}
                    </v-card-subtitle>
                    <v-file-input v-model="uploadFileContractConditions" accept="application/pdf"
                        label="Contract Condition" placeholder="Please select a PDF file" show-size></v-file-input>
                    <v-select v-model="currentBuilding.internationalOnly" :items="internationalOnlyItems"
                        label="International Only" :rules="internationalOnlyRules" required>
                    </v-select>
                    <v-select v-model="currentBuilding.enabled" :items="enabledItems" label="Enabled"
                        :rules="enabledRules" required>
                    </v-select>
                    <v-textarea v-model="currentBuilding.notes" auto-grow counter="512" label="Notes" :rules="noteRules"
                        outlined>
                    </v-textarea>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn ref="confirmBtn" class="white--text" :color="successColor" :disabled="!valid"
                    @click="editBuilding()">
                    Save
                </v-btn>
                <v-btn :color="warningColor" @click="closeDialog()">
                    Cancel
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'buildings-edit',
    props: {
        item: {
            required: true,
            type: Object
        }
    },
    data() {
        return {
            currentBuilding: '',
            editBuildingDialog: false,
            enabledItems: [{
                    text: 'Yes',
                    value: 1
                },
                {
                    text: 'No',
                    value: 0
                }
            ],
            enabledRules: [
                v => !(v === "") || 'Enabled is required',
            ],
            internationalOnlyItems: [{
                    text: 'Yes',
                    value: 1
                },
                {
                    text: 'No',
                    value: 0
                }
            ],
            internationalOnlyRules: [
                v => !(v === "") || 'International only is required',
            ],
            maxOccupancyRules: [
                v => !(v === "") || 'Max occupancy is required',
                v => (!(v === "") && v >= 0 && v <= 300) || 'Max occupancy must be between 0 and 300',
            ],
            maxRoommateRequestItems: [{
                    text: '1',
                    value: 1
                },
                {
                    text: '2',
                    value: 2
                },
                {
                    text: '3',
                    value: 3
                },
                {
                    text: '4',
                    value: 4
                },
                {
                    text: '5',
                    value: 5
                }
            ],
            maxRoommateRequestRules: [
                v => !(v === "") || 'Max roommate request is required',
            ],
            minYearOfStudyItems: [{
                    text: '1',
                    value: 1
                },
                {
                    text: '2',
                    value: 2
                },
                {
                    text: '3',
                    value: 3
                },
                {
                    text: '4',
                    value: 4
                }
            ],
            mealPlanRequiredItems: [{
                    text: 'Yes',
                    value: 1
                },
                {
                    text: 'No',
                    value: 0
                }
            ],
            mealPlanRequiredRules: [
                v => !(v === "") || 'Meal plan required is required',
            ],
            minYearOfStudyRules: [
                v => !(v === "") || 'Min year of study is required',
            ],
            nameRules: [
                v => !!v || 'Name is required',
                v => (v && v.length >= 2) || 'Name must be more than 2 characters',
            ],
            noteRules: [
                v => (!v || (v && v.length >= 0 && v.length <= 512)) || 'Note must be between 0 and 512 characters',
            ],
            uploadFileContractConditions: '',
            valid: true
        }
    },
    methods: {
        closeDialog() {
            this.editBuildingDialog = false;
        },
        editBuilding() {
            const that = this;
            const formData = new FormData();
            let api = `https://${location.hostname}/cgi-bin/res-app-api/controller.api.pl?setBuilding`;

            if (this.$refs.editBuildingForm.validate()) {
                // Parse array into string
                formData.append('mode', 'setBuilding');
                formData.append('data', JSON.stringify(this.currentBuilding));
                formData.append('uploadFileContractConditions', this.uploadFileContractConditions);
                that.$refs.confirmBtn.loading = true;

                // Post request
                fetch(api, {
                        method: 'POST',
                        body: formData
                    })
                    .then(function (response) {
                        // Return JSON response
                        return response.json();
                    })
                    .then(function (json) {
                        // Success result
                        if (json.result.match(/OK/i)) {
                            that.$emit('setNotification', '#1dc9b7', 'mdi-check-circle', `${that.currentBuilding.name} has been successfully updated.`, true);
                        } else if (json.message.match(/UNAUTHORIZED/i)) {
                            that.$emit('setNotification', '#f64e60', 'mdi-alert-outline', `You are not authorized to perform this action. Please refresh your application.`, true);
                        } else {
                            that.$emit('setNotification', '#ffb822', 'mdi-alert-circle', `${that.currentBuilding.name} could not be updated. ${json.message}.`, true);
                        }

                        that.$emit('refreshData');
                        that.$refs.confirmBtn.loading = false;
                        that.closeDialog();
                    })
                    .catch(function () {
                        that.$emit('refreshData');
                        that.$refs.confirmBtn.loading = false;
                        that.$emit('setNotification', '#f64e60', 'mdi-alert-outline', `An error occurred. Please contact your system administrator.`, true);
                    });
            }
        },
        getContractConditionsFileDate() {
            if (this.currentBuilding && this.currentBuilding.fileContractConditions && this.currentBuilding.fileContractConditions.uploadedTimestamp) {
                let date = new Date(this.currentBuilding.fileContractConditions.uploadedTimestamp * 1000);
                return date.toDateString();
            }

            return `None`;
        },
        getContractConditionsFilename() {
            if (this.currentBuilding && this.currentBuilding.fileContractConditions && this.currentBuilding.fileContractConditions.file) {
                return this.currentBuilding.fileContractConditions.file;
            }

            return `None`;
        },
        getContractConditionsUploadedBy() {
            if (this.currentBuilding && this.currentBuilding.fileContractConditions && this.currentBuilding.fileContractConditions.uploadedByUsername) {
                return this.currentBuilding.fileContractConditions.uploadedByUsername;
            }

            return `None`;
        },
        setBuilding() {
            this.currentBuilding = {
                ...this.item
            };

            this.uploadFileContractConditions = '';

            if (this.$refs.editBuildingForm) {
                this.$refs.editBuildingForm.resetValidation();
            }
        }
    },
    computed: {
        dangerColor() {
            return '#f64e60';
        },
        primaryColor() {
            return '#5867dd';
        },
        successColor() {
            return '#1dc9b7';
        },
        warningColor() {
            return '#ffb822'
        }
    }
}
</script>

<style scoped>
.v-input--checkbox {
    margin-top: 0;
}
</style>